import React, {useContext, useEffect, useState} from 'react';
import {Button, Form, Input, message, Modal, Select, Table, Tag, Typography} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {UserContext} from "../../context/UserContext";
import api from "../../utils/api";
import {useNavigate} from "react-router-dom";

const {Option} = Select;

const {Column, ColumnGroup} = Table;

const layout = {
    labelCol: {span: 8},
    wrapperCol: {span: 16},
};

const {Title} = Typography;

const UsersListPage = () => {
    const {user} = useContext(UserContext);
    const [registeredUsers, setRegisteredUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [form] = Form.useForm();

    const navigate = useNavigate();
    const [organizationInfo, setOrganizationInfo] = useState({});


    const fetchOrganizationUsers = async () => {
        setLoading(true);
        try {
            const [registeredUsersResponse] = await Promise.all([
                api.get(`/organizations/${user.organizationId}/registered_users`),
            ]);

            setRegisteredUsers(registeredUsersResponse.data.users);
        } catch (error) {
            console.error("Error fetching organization users:", error);
        } finally {
            setLoading(false);
        }
    };

    const getOrganizationInfo = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/organizations/${user.organizationId}`);
            setOrganizationInfo(response.data.organizations);
            return response.data.organizations;
        } catch (error) {
            console.error("Error fetching organization info:", error);
        } finally {
            setLoading(false);
        }
    }

    const showModal = () => {
        // Initialize the form with default values when opening modal
        form.setFieldsValue({
            role: "DOCTOR"
        });
        setOpen(true);
    };

    const handleOk = async () => {
        try {
            // This will validate all fields and call onFinish if validation passes
            const values = await form.validateFields();
            onFinish(values);
        } catch (errorInfo) {
            // Validation failed
            console.log('Validation failed:', errorInfo);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setOpen(false);
    };

    const onFinish = async (values) => {
        setConfirmLoading(true);
        try {
            await api.post('/invitations', {
                organization_id: user.organizationId,
                email: values.email,
                role: values.role
            });

            // Show success message
            message.success('User invitation sent successfully');

            // Refresh the invitations list
            await fetchOrganizationUsers();

            // Reset form and close modal
            form.resetFields();
            setOpen(false);
        } catch (error) {
            console.error("Error inviting user:", error);
            message.error('Failed to send invitation');
        } finally {
            setConfirmLoading(false);
        }
    };

    function checkRole(role) {
        switch (role) {
            case 1:
                return "DOCTOR";
            case 2:
                return "ADMIN";
            case 3:
                return "ORGANIZATION_ADMIN";
            case 4:
                return "ANNOTATOR";
            case 5:
                return "PATIENT";
            default:
                return "UNKNOWN";
        }
    }

    function checkStatus(status) {
        switch (status) {
            case 1:
                return "PENDING";
            case 2:
                return "ACCEPTED";
            default:
                return "UNKNOWN";
        }
    }

    const RenderStatusTag = (status) => {
        if (status === 1) {
            return <Tag color="processing">PENDING</Tag>;
        } else if (status === 2) {
            return <Tag color="success">ACCEPTED</Tag>;
        }
        return null;
    }

    useEffect(() => {
        if (user?.organizationId) {
            getOrganizationInfo();
            fetchOrganizationUsers();
        }
    }, [user]);

    return (
        <>
            <div className="h-screen overflow-auto w-full">
                <div className="mt-3 w-full cardContainer">
                    <div className="flex justify-between">
                        <h1 className="text-2xl font-bold">Organization Users</h1>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={showModal}>Invite</Button>
                    </div>
                    <div className="flex gap-3 w-full mt-3 flex-col">

                        <Table dataSource={registeredUsers} rowKey="id" loading={loading}
                               onRow={(record, rowIndex) => {
                                   return {
                                       onClick: (event) => {
                                           // Handle row click
                                           console.log('Row clicked:', record);
                                           navigate(`${record.id}`);
                                       }, // click row
                                   };
                               }}
                        >
                            <ColumnGroup title="Name">
                                <Column title="First Name" dataIndex="firstName" key="firstName"/>
                                <Column title="Last Name" dataIndex="lastName" key="lastName"/>
                            </ColumnGroup>
                            <Column title="Email" dataIndex="email" key="email"/>
                            <Column title="Role" dataIndex="role" key="role"
                                    render={(text, record) => checkRole(record.role)}/>
                        </Table>
                    </div>
                </div>
            </div>
            <Modal
                title="Invite User"
                open={open}
                onOk={handleOk}
                confirmLoading={confirmLoading}
                onCancel={handleCancel}
            >
                <Form
                    {...layout}
                    form={form}
                    name="invite-user-form"
                    initialValues={{role: "DOCTOR"}}
                    style={{maxWidth: 600}}
                >
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            {required: true, message: 'Please input an email address!'},
                            {type: 'email', message: 'Please enter a valid email address!'}
                        ]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="role"
                        label="Role"
                        rules={[{required: true, message: 'Please select a role!'}]}
                    >
                        <Select placeholder="Select a role">
                            <Option value="DOCTOR">DOCTOR</Option>
                            <Option value="ORGANIZATION_ADMIN">ORGANIZATION_ADMIN</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default UsersListPage;